import { useEffect, useRef, useState } from "react";
import axios from "axios";

// ui
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

// Bar
import TableTopBar from "../component/TableTopBar";
import { DataAddModalResult } from "../component/DataAddModalResult";

// jotai
import { useAtomValue, useSetAtom } from "jotai";
import { AppAtoms } from "../lib/store";

// API
import {
  Inquiry as InquiryType,
  getInquiries,
  getInquiry,
  postInquiry,
} from "../api/inquiry";
import { getPresignedGetPicture, postPresignedPost } from "../api/presignedURL";
import { CONST } from "../config/const";

const CATEGORIES = ["機能改善要望", "バグ報告", "操作方法の質問", "その他"];

interface ProductUpdate {
  id: number;
  type: string;
  title: string;
  description: string;
  category?: string | null;
  release_date?: string | null;
  image_url?: string | null;
}

function Inquiry() {
  const setIsLoading = useSetAtom(AppAtoms.isLoading);
  const loginUser = useAtomValue(AppAtoms.loginUser);

  const isAdminOrStaff =
    loginUser?.authority === "マスターアカウント" ||
    loginUser?.authority === "スタッフアカウント";

  // 一覧
  const [inquiries, setInquiries] = useState<InquiryType[]>([]);
  const [listLoading, setListLoading] = useState(false);

  // 投稿フォームモーダル
  const [postModalOpen, setPostModalOpen] = useState(false);
  const [postLoading, setPostLoading] = useState(false);
  const [postCategory, setPostCategory] = useState("");
  const [postSubject, setPostSubject] = useState("");
  const [postContent, setPostContent] = useState("");

  // 画像添付
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);

  // 詳細モーダル
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState<InquiryType | null>(
    null,
  );
  const [detailImageUrl, setDetailImageUrl] = useState<string | null>(null);

  // 投稿成功ダイアログ
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [addModalResult, setAddModalResult] = useState('success');

  // 改修予定・改修履歴
  const [productUpdates, setProductUpdates] = useState<ProductUpdate[]>([]);
  const [updatesLoading, setUpdatesLoading] = useState(false);

  // 画像拡大モーダル
  const [imageModalUrl, setImageModalUrl] = useState<string | null>(null);

  const fetchInquiries = async () => {
    setListLoading(true);
    const result = await getInquiries();
    if (result) {
      setInquiries(result.inquiries ?? []);
    }
    setListLoading(false);
  };

  const fetchProductUpdates = async () => {
    setUpdatesLoading(true);
    try {
      const response = await axios.get(CONST.apiUrl2 + "productUpdates");
      setProductUpdates(response.data.product_updates ?? []);
    } catch {
      setProductUpdates([]);
    }
    setUpdatesLoading(false);
  };

  useEffect(() => {
    fetchInquiries();
  }, []);

  useEffect(() => {
    fetchProductUpdates();
  }, []);

  const handleOpenInquiryWithProduct = (item: ProductUpdate) => {
    setPostCategory("機能改善要望");
    setPostSubject(`【${item.title}について】`);
    setPostModalOpen(true);
  };

  // 画像選択
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setImageError("ファイルサイズは5MB以下にしてください");
      setSelectedFile(null);
      setImagePreviewUrl(null);
      return;
    }
    setImageError(null);
    setSelectedFile(file);
    setImagePreviewUrl(URL.createObjectURL(file));
  };

  // 行クリックで詳細表示
  const handleRowClick = async (inquiry: InquiryType) => {
    setDetailLoading(true);
    setDetailModalOpen(true);
    setSelectedInquiry(null);
    setDetailImageUrl(null);
    const result = await getInquiry(inquiry.id);
    if (result) {
      setSelectedInquiry(result.inquiry);
      if (result.inquiry.image_s3_key) {
        const url = await getPresignedGetPicture(result.inquiry.image_s3_key);
        if (url) setDetailImageUrl(url);
      }
    }
    setDetailLoading(false);
  };

  // 投稿フォーム送信
  const handlePostSubmit = async () => {
    if (!postCategory || !postSubject || !postContent) return;
    setPostModalOpen(false);
    setIsLoading(true);

    let image_s3_key: string | undefined = undefined;

    if (selectedFile) {
      try {
        const response = await axios.get(
          CONST.apiCognitoUrl + "presigned_post/picture",
          {
            params: {
              prefix: "inquiry",
              nowTimeName: Date.now().toString(),
              fileName: selectedFile.name,
            },
          },
        );
        const presigned_object = response.data.presigned_object as {
          fields: { [key: string]: string };
          url: string;
        };
        const destination_key = response.data.destination_key as string;

        const formData = new FormData();
        if (presigned_object?.fields) {
          Object.entries(presigned_object.fields).forEach(([k, v]) =>
            formData.append(k, v as string),
          );
        }
        formData.append("file", selectedFile);
        await postPresignedPost(presigned_object.url, formData);
        image_s3_key = destination_key;
      } catch {
        // 画像アップロード失敗時も投稿は続行
      }
    }

    const result = await postInquiry({
      category: postCategory,
      subject: postSubject,
      content: postContent,
      ...(image_s3_key ? { image_s3_key } : {}),
    });

    setIsLoading(false);
    setPostCategory("");
    setPostSubject("");
    setPostContent("");
    setSelectedFile(null);
    setImagePreviewUrl(null);
    setImageError(null);
    fetchInquiries();
    setAddModalResult(result || 'success');
    setSuccessDialogOpen(true);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    return dateStr.replace("T", " ").substring(0, 16);
  };

  const groupByCategory = (items: ProductUpdate[]) => {
    return items.reduce(
      (acc, item) => {
        const cat = item.category ?? "未分類";
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(item);
        return acc;
      },
      {} as Record<string, ProductUpdate[]>,
    );
  };

  const plannedUpdates = productUpdates.filter((u) => u.type === "planned");
  const completedUpdates = productUpdates.filter((u) => u.type === "completed");

  return (
    <Box sx={{ width: "100%" }} data-testid="inquiry-page">
      <TableTopBar
        serch={false}
        serchOpen={false}
        setSerchOpen={() => {}}
        addDataNames={[]}
        addDataName={""}
        setAddDataName={() => {}}
        addCSVDataNames={[]}
        addCSVDataName={""}
        setAddCSVDataName={() => {}}
        getXLSXDataNames={[]}
        getXLSXDataName={""}
        setGetXLSXDataName={() => {}}
        editDatasNames={[]}
        editDatasName={""}
        setEditDatasName={() => {}}
        editIDs={[]}
        columnSettingOpen={false}
        openColumnSettingWindow={() => {}}
      />
      <Box sx={{ marginTop: "0px", padding: "16px" }}>
        {/* 案内文言 */}
        <Alert severity="info" sx={{ marginBottom: "16px" }}>
          問い合わせしていただいた内容は毎週金曜日に集約し、今後のよりよいシステム作りのための参考にさせていただきます。
          <br />
          ※エントリー情報やトスアップ情報など、登録したデータに関するお問い合わせはお答えできかねます。
        </Alert>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <Button
            variant="contained"
            sx={{ color: "white" }}
            onClick={() => {
              setPostModalOpen(true);
            }}
          >
            問い合わせを投稿
          </Button>
        </Box>

        {listLoading ? (
          <Box
            sx={{ display: "flex", justifyContent: "center", padding: "40px" }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "rgb(230, 230, 240)" }}>
                <TableCell>件名</TableCell>
                <TableCell>カテゴリ</TableCell>
                <TableCell>ステータス</TableCell>
                <TableCell>投稿日時</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inquiries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography color="text.secondary">
                      データがありません
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                inquiries.map((inquiry) => (
                  <TableRow
                    key={inquiry.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleRowClick(inquiry)}
                  >
                    <TableCell>{inquiry.subject}</TableCell>
                    <TableCell>{inquiry.category}</TableCell>
                    <TableCell>
                      <Chip
                        label={
                          inquiry.status === "open" ? "未確認" : "確認済み"
                        }
                        color={
                          inquiry.status === "open" ? "warning" : "default"
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(inquiry.created_at)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}

        {/* 改修予定・改修履歴セクション */}
        <Box sx={{ marginTop: "40px" }}>
          {updatesLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                padding: "20px",
              }}
            >
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box sx={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>
              {/* 今後の改修予定 */}
              <Box sx={{ flex: 1, minWidth: "280px" }}>
                <Typography
                  variant="h6"
                  sx={{ marginBottom: "8px", fontWeight: "bold" }}
                >
                  【今後の改修予定】
                </Typography>
                {plannedUpdates.length === 0 ? (
                  <Typography color="text.secondary" variant="body2">
                    現在予定はありません
                  </Typography>
                ) : (
                  Object.entries(groupByCategory(plannedUpdates)).map(
                    ([cat, items]) => (
                      <Box key={cat} sx={{ mb: 2 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: "bold",
                            mb: 1,
                            color: "primary.main",
                          }}
                        >
                          {cat}
                        </Typography>
                        {items.map((item) => (
                          <Box
                            key={item.id}
                            sx={{
                              marginBottom: "8px",
                              padding: "8px 12px",
                              backgroundColor: "rgb(240, 245, 255)",
                              borderRadius: "4px",
                              borderLeft: "4px solid #1976d2",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                              }}
                            >
                              <Typography variant="body2" fontWeight="bold">
                                {item.title}
                              </Typography>
                            </Box>
                            {item.description && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ whiteSpace: "pre-wrap" }}
                              >
                                {item.description}
                              </Typography>
                            )}
                            {item.image_url && (
                              <Box sx={{ marginTop: "8px" }}>
                                <img
                                  src={item.image_url}
                                  alt={item.title}
                                  style={{
                                    maxWidth: "200px",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                  }}
                                  onClick={() =>
                                    setImageModalUrl(item.image_url!)
                                  }
                                />
                              </Box>
                            )}
                            <Box sx={{ marginTop: "8px" }}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() =>
                                  handleOpenInquiryWithProduct(item)
                                }
                              >
                                この機能について問い合わせ
                              </Button>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    ),
                  )
                )}
              </Box>

              {/* 改修履歴 */}
              <Box sx={{ flex: 1, minWidth: "280px" }}>
                <Typography
                  variant="h6"
                  sx={{ marginBottom: "8px", fontWeight: "bold" }}
                >
                  【改修履歴】
                </Typography>
                {completedUpdates.length === 0 ? (
                  <Typography color="text.secondary" variant="body2">
                    履歴はありません
                  </Typography>
                ) : (
                  Object.entries(groupByCategory(completedUpdates)).map(
                    ([cat, items]) => (
                      <Box key={cat} sx={{ mb: 2 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: "bold",
                            mb: 1,
                            color: "primary.main",
                          }}
                        >
                          {cat}
                        </Typography>
                        {items.map((item) => (
                          <Box
                            key={item.id}
                            sx={{
                              marginBottom: "8px",
                              padding: "8px 12px",
                              backgroundColor: "rgb(240, 250, 240)",
                              borderRadius: "4px",
                              borderLeft: "4px solid #4caf50",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                              }}
                            >
                              <Typography variant="body2" fontWeight="bold">
                                {item.release_date
                                  ? `${item.release_date}: `
                                  : ""}
                                {item.title}
                              </Typography>
                            </Box>
                            {item.description && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ whiteSpace: "pre-wrap" }}
                              >
                                {item.description}
                              </Typography>
                            )}
                            {item.image_url && (
                              <Box sx={{ marginTop: "8px" }}>
                                <img
                                  src={item.image_url}
                                  alt={item.title}
                                  style={{
                                    maxWidth: "200px",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                  }}
                                  onClick={() =>
                                    setImageModalUrl(item.image_url!)
                                  }
                                />
                              </Box>
                            )}
                            <Box sx={{ marginTop: "8px" }}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() =>
                                  handleOpenInquiryWithProduct(item)
                                }
                              >
                                この機能について問い合わせ
                              </Button>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    ),
                  )
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* 投稿フォームモーダル */}
      <Dialog
        open={postModalOpen}
        onClose={() => setPostModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>問い合わせを投稿</DialogTitle>
        <DialogContent>
          <FormControl
            fullWidth
            sx={{ marginTop: "16px", marginBottom: "16px" }}
          >
            <InputLabel>カテゴリ</InputLabel>
            <Select
              value={postCategory}
              label="カテゴリ"
              onChange={(e: SelectChangeEvent) =>
                setPostCategory(e.target.value)
              }
            >
              {CATEGORIES.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="件名"
            value={postSubject}
            onChange={(e) => setPostSubject(e.target.value)}
            sx={{ marginBottom: "16px" }}
          />
          <TextField
            fullWidth
            label="本文"
            value={postContent}
            onChange={(e) => setPostContent(e.target.value)}
            multiline
            rows={4}
            sx={{ marginBottom: "16px" }}
          />

          {/* 画像添付 */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", marginBottom: "8px" }}
          >
            スクリーンショットや画像を添付いただくと、より迅速に改修対応できます
          </Typography>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageSelect}
            style={{ display: "none" }}
            ref={fileInputRef}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={() => fileInputRef.current?.click()}
          >
            画像を添付
          </Button>
          {imageError && (
            <Typography
              color="error"
              variant="caption"
              sx={{ display: "block", marginTop: "4px" }}
            >
              {imageError}
            </Typography>
          )}
          {imagePreviewUrl && (
            <Box sx={{ marginTop: "8px" }}>
              <img
                src={imagePreviewUrl}
                alt="preview"
                style={{
                  maxWidth: "120px",
                  maxHeight: "120px",
                  objectFit: "contain",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block" }}
              >
                {selectedFile?.name}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={handlePostSubmit}
            disabled={
              !postCategory || !postSubject || !postContent || !!imageError
            }
            sx={{ color: "#fff" }}
          >
            送信
          </Button>
          <Button
            onClick={() => {
              setPostModalOpen(false);
              setSelectedFile(null);
              setImagePreviewUrl(null);
              setImageError(null);
            }}
          >
            キャンセル
          </Button>
        </DialogActions>
      </Dialog>

      {/* 投稿成功モーダル */}
      <DataAddModalResult
        onModal={successDialogOpen}
        swCloseModal={() => setSuccessDialogOpen(false)}
        addModalTitle="問い合わせ投稿完了"
        onDataAddModalResult={addModalResult}
      />

      {/* 詳細モーダル */}
      <Dialog
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        {detailLoading ? (
          <DialogContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                padding: "40px",
              }}
            >
              <CircularProgress />
            </Box>
          </DialogContent>
        ) : selectedInquiry ? (
          <>
            <DialogTitle sx={{ pb: 1, pr: "48px" }}>
              {selectedInquiry.subject}
            </DialogTitle>
            <DialogContent dividers sx={{ pt: "12px" }}>
              {isAdminOrStaff && selectedInquiry.vendor_company_name && (
                <Box sx={{ marginBottom: "12px" }}>
                  <Typography variant="caption" color="text.secondary">
                    ベンダー会社名
                  </Typography>
                  <Typography>{selectedInquiry.vendor_company_name}</Typography>
                </Box>
              )}
              <Box sx={{ marginBottom: "12px" }}>
                <Typography variant="caption" color="text.secondary">
                  カテゴリ
                </Typography>
                <Typography>{selectedInquiry.category}</Typography>
              </Box>
              <Box sx={{ marginBottom: "12px" }}>
                <Typography variant="caption" color="text.secondary">
                  ステータス
                </Typography>
                <Box>
                  <Chip
                    label={
                      selectedInquiry.status === "open" ? "未確認" : "確認済み"
                    }
                    color={
                      selectedInquiry.status === "open" ? "warning" : "default"
                    }
                    size="small"
                  />
                </Box>
              </Box>
              <Box sx={{ marginBottom: "12px" }}>
                <Typography variant="caption" color="text.secondary">
                  本文
                </Typography>
                <Typography sx={{ whiteSpace: "pre-wrap" }}>
                  {selectedInquiry.content}
                </Typography>
              </Box>
              {detailImageUrl && (
                <Box sx={{ marginBottom: "12px" }}>
                  <Typography variant="caption" color="text.secondary">
                    添付画像
                  </Typography>
                  <Box sx={{ marginTop: "4px" }}>
                    <img
                      src={detailImageUrl}
                      alt="添付画像"
                      style={{ maxWidth: "100%", borderRadius: "4px" }}
                    />
                  </Box>
                </Box>
              )}
              <Box sx={{ marginBottom: "12px" }}>
                <Typography variant="caption" color="text.secondary">
                  投稿日時
                </Typography>
                <Typography>
                  {formatDate(selectedInquiry.created_at)}
                </Typography>
              </Box>
              {selectedInquiry.closed_at && (
                <Box sx={{ marginBottom: "12px" }}>
                  <Typography variant="caption" color="text.secondary">
                    対応完了日時
                  </Typography>
                  <Typography>
                    {formatDate(selectedInquiry.closed_at)}
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailModalOpen(false)}>閉じる</Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>

      {/* 画像拡大モーダル */}
      <Dialog
        open={imageModalUrl !== null}
        onClose={() => setImageModalUrl(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent sx={{ padding: "8px" }}>
          <img
            src={imageModalUrl ?? ""}
            alt="拡大画像"
            style={{ maxWidth: "100%", maxHeight: "80vh", display: "block" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageModalUrl(null)}>閉じる</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Inquiry;
